import React from 'react'
import moment from 'moment-timezone'
import TimePicker from 'antd/es/time-picker'
import { momentLocalTimeFormat, range } from 'libs/utils'
import BaseField from './BaseField'


export default function FieldTimeRange(props) {
  /* Input Time
   * Expected Props:
   * className = <string> Optional
   * label = <string> Required
   * required = <boolean> Optional
   * name = <string> Required
   * value = <string> Required
   * value2 = <string> Required
   * placeholder = <string> Optional
   * valid = <boolean> Optional
   * validMessage = <string> Optional
   * invalidMessage = <string> Optional
   * help = <string> Optional
   * helpAboveInput = <boolean> Optional
   * onChange = <function()> Required
   * onChange2 = <function()> Required
   * onBlur = <function()> Optional
   * disabled = <boolean> Optional
   * readOnly = <boolean> Optional
   * prepend = <string> Optional
   * append = <string> Optional
   * min = <integer> Optional
   * max = <integer> Optional
   * horizontal = <boolean> Optional
   * horizontalLabelSize = <int [1-23]> Optional (Default = 6)
   * minuteStep = <int> Optional (Default = 5)
   */
  const ignoreOffset = props.type === "time_range"
  const toString = value => value?.toISOString(ignoreOffset).substr(11, 5) || null
  const fromString = value => !value ? "" : props.timezone ?
    moment.tz(ignoreOffset ? value : `${value}Z`, "HH:mmZ", props.timezone)
    :
    moment(ignoreOffset ? value : `${value}Z`, "HH:mmZ")

  // Memos
  const timezoneAbbr = React.useMemo(() => {
    if (!props.timezone) return ""
    const newTimezoneOffset = moment.tz(props.timezone).utcOffset()
    return newTimezoneOffset !== moment().utcOffset() ? ` [GMT${newTimezoneOffset >= 0 ? "+" : ""}${(newTimezoneOffset / 60).toFixed(2)}]` : ""
  }, [props.timezone])

  const time = React.useMemo(() => fromString(props.value), [props.value, props.timezone])

  const time2 = React.useMemo(() => fromString(props.value2), [props.value2, props.timezone])

  const minTime = React.useMemo(() => fromString(props.min), [props.min, props.timezone])

  const maxTime = React.useMemo(() => fromString(props.max), [props.max, props.timezone])

  // Functions
  const handleChange = ([nextStartTime, nextEndTime]) => {
    if (props.timezone) {
      nextStartTime?.tz(props.timezone, true)
      nextEndTime?.tz(props.timezone, true)
    }
    props.onChange({ target: { value: toString(nextStartTime) } })
    props.onChange2({ target: { value: toString(nextEndTime) } })
  }

  // - Check min/max time
  const getHourValue = (date) => date ? Number(date.toISOString(true).slice(11, 13)) : null
  const getMinuteValue = (date) => date ? Number(date.toISOString(true).slice(14, 16)) : null
  const disabledHours = () => {
    const minHour = minTime ? getHourValue(minTime) : 0
    const maxHour = maxTime ? getHourValue(maxTime) : 23
    return range(0, 23).filter(h => h < minHour || h > maxHour)
  }
  const disabledMinutes = (hour) => {
    if (hour < 0) return range(0, 59)
    const minMinute = minTime && hour === getHourValue(minTime) ? getMinuteValue(minTime) : 0
    const maxMinute = maxTime && hour === getHourValue(maxTime) ? getMinuteValue(maxTime) : 59
    return range(0, 59).filter(m => m < minMinute || m > maxMinute)
  }

  // Render
  return (
    <BaseField
      className={props.className}
      labelClassName={props.labelClassName}
      inputClassName={props.inputClassName}
      label={props.label}
      input={
        <TimePicker.RangePicker
          className={"form-control custom-field-input" + (
            props.valid === true ? " is-valid" : props.valid === false ? " is-invalid" : ""
          )}
          format={`${momentLocalTimeFormat}${timezoneAbbr}`}
          minuteStep={props.minuteStep || 5}
          placeholder={props.placeholder}
          value={[time, time2]}
          onChange={handleChange}
          onBlur={props.onBlur}
          disabled={props.disabled || props.readOnly}
          inputReadOnly={props.readOnly}
          disabledHours={disabledHours}
          disabledMinutes={disabledMinutes}
        />
      }
      prepend={props.prepend}
      append={props.append}
      disabled={props.disabled}
      readOnly={props.readOnly}
      required={props.required}
      valid={props.valid}
      validMessage={props.validMessage}
      invalidMessage={props.invalidMessage}
      help={props.help}
      helpAboveInput={props.helpAboveInput}
      horizontal={props.horizontal}
      horizontalLabelSize={props.horizontalLabelSize}
    />
  )
}
